/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    REVIEW_USER_HEADER_COMP
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Extracted header section for ReviewUserForm to comply with file size limits.
 * Contains user avatar, name, title, social icons, and edit button.
 *
 * File Size Check:
 * - Current: ~110 lines
 * - Limit: 200 lines
 * - Decision: New component creation (under limit)
 *
 * Related Screens / Modules:
 * - client/src/components/setup/ReviewUserForm.tsx
 */
import React from "react";
import { Button } from "react-bootstrap";
import type { User, ImageData } from "../../../../interfaces/User";

interface ReviewUserHeaderProps {
  profile: Partial<User> | null;
  onEdit: () => void;
  getImageSrc: (img?: ImageData | null) => string | null;
}

export const ReviewUserHeader: React.FC<ReviewUserHeaderProps> = ({ 
  profile, 
  onEdit, 
  getImageSrc 
}) => {
  const profileImage = (profile as any)?.profileImages as ImageData | undefined;

  return (
    <>
      <div className="review-page-header d-flex justify-content-end mb-4">
        <Button 
          variant="outline-secondary" 
          className="review-edit-button"
          onClick={onEdit}
          aria-label="Edit profile"
        >
          <img src="/assets/editprofile.svg" alt="Edit" width={32} height={32} />
        </Button>
      </div>

      <div className="d-flex align-items-start justify-content-between mb-3">
        <div className="d-flex align-items-center gap-3">
          {profileImage && (
            <div className="review-avatar">
              <img src={getImageSrc(profileImage) || ""} alt="Profile" />
            </div>
          )}
          <div>
            <h4 className="review-profile-name mb-1">
              {profile?.firstName || profile?.lastName
                ? `${profile?.firstName || ""} ${profile?.lastName || ""}`.trim()
                : "Your name"}
            </h4>
            <div className="review-profile-title">
              {profile?.jobTitle || "Your position"}, {profile?.companyName || ""}
            </div>
            
            <div className="review-profile-social-icons">
              {profile?.linkedIn && (
                <a 
                  href={profile.linkedIn} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="review-profile-social-icon"
                  aria-label="LinkedIn profile"
                >
                  <img src="/assets/social.svg" alt="LinkedIn" />
                </a>
              )}
              {profile?.facebook && (
                <a 
                  href={profile.facebook} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="review-profile-social-icon"
                  aria-label="Facebook profile"
                >
                  <img src="/assets/social (1).svg" alt="Facebook" />
                </a>
              )}
              {profile?.x && (
                <a 
                  href={profile.x} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="review-profile-social-icon"
                  aria-label="X (Twitter) profile"
                >
                  <img src="/assets/social (2).svg" alt="X / Twitter" />
                </a>
              )}
              {profile?.instagram && (
                <a 
                  href={profile.instagram} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="review-profile-social-icon"
                  aria-label="Instagram profile"
                >
                  <img src="/assets/social (3).svg" alt="Instagram" />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
